import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { ViewerTopFragmentCommonProps } from "../viewer-top-fragment/ViewerTopFragment";
import { ItemCardWithCopyNo } from "@coworker/apprestructured/src/shared/components/item-cards/ItemCardWithCopyNo/ItemCardWithCopyNo";
import { ItemNoAndType } from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { useItemInfo } from "@coworker/apprestructured/src/shared/hooks/item/useItemInfo";
import TopFragmentWrapper from "../../../SheetTop/TopFragmentWrapper/TopFragmentWrapper";
import TaskState from "@coworker/apprestructured/src/task-management/types/TaskState";
import styles from "./ArticleViewerTopFragment.module.css";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import FixaInlineMessage from "@coworker/apprestructured/src/shared/wrappers/FixaInlineMessage/FixaInlineMessage";
import { useTranslation } from "@coworker/locales";

export type ArticleViewerTopFragmentConfigProps = {
  field: string;
  // Enables showing the "Article removed" inline message when the article
  // can't be resolved anymore. Off by default so non-media task types keep
  // rendering nothing in that case, unchanged.
  isInlineMessageEnabled?: boolean;
};

type ArticleViewerTopFragmentProps = {
  configProps: ArticleViewerTopFragmentConfigProps;
  commonProps: ViewerTopFragmentCommonProps;
};

function ArticleViewerTopFragment({
  configProps,
  commonProps,
}: ArticleViewerTopFragmentProps) {
  const { field, isInlineMessageEnabled } = configProps;
  const { task } = commonProps;
  const { t } = useTranslation();
  const itemNoAndType = getTypedValue<ItemNoAndType>(getByPath(task, field));
  const state = getTypedValue<TaskState>(getByPath(task, "state"));

  const {
    data: item,
    isLoading: isFetchingItem,
    isError: isErrorFetchingItem,
  } = useItemInfo((itemNoAndType as ItemNoAndType)?.no);

  if (isFetchingItem) return null;

  // A transient fetch failure isn't proof the article was deleted -- don't
  // show a misleading "removed" message for it.
  if (isErrorFetchingItem) return null;

  if (!item) {
    return isInlineMessageEnabled ? (
      <TopFragmentWrapper state={state}>
        <div className={styles["message"]}>
          <FixaInlineMessage
            variant="negative"
            title={t("articleRemovedString")}
            body={t("articleRemovedInTaskDescriptionString")}
          />
        </div>
      </TopFragmentWrapper>
    ) : null;
  }

  return (
    <TopFragmentWrapper state={state}>
      <div className={styles["card"]}>
        <ItemCardWithCopyNo item={item} />
      </div>
    </TopFragmentWrapper>
  );
}

export default ArticleViewerTopFragment;
